/**
 * @file ChildPortal.js
 * @description A dual-mode component that can display content either as an icon-based quick link or as a rich text content panel with optional image carousel. Uses TipTap editor to render read-only formatted content.
 */
import React, { useContext, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import {
    Color,
    FontFamily,
    FontSize,
    TextStyle,
} from '@tiptap/extension-text-style';
import { ListItem } from '@tiptap/extension-list';
import { Placeholder } from '@tiptap/extensions';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import {
    Table,
    TableCell,
    TableHeader,
    TableRow,
} from '@tiptap/extension-table';
import ImageCarousel from '../multimedia-component/ImageCarousel';
import './ChildPortal.css';
import Emoji from '../../utils/tiptap/EmojiExtension';
import { useAuthContext } from '../../store/Contexts';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import ResizableImage from '../../utils/tiptap/ResizableImageExtension';
import { VideoNode } from '../new-portal-page/VideoNode';
import { QRCodeNode } from '../new-portal-page/QRCodeNode';
import DivParagraph from '../new-portal-page/DivParagraph';
import { getText } from '../../i18n';

function isAssetAnchor(anchor) {
    if (!anchor) {
        return false;
    }

    if (anchor.getAttribute('data-link-type') === 'asset') {
        return true;
    }

    const href = anchor.getAttribute('href') || '';
    return /\/files\/asset\//i.test(href);
}

const ChildPortal = ({
    title,
    icon,
    foregroundColor,
    backgroundColor,
    images,
    caption,
    text,
    button,
    link,
    handleSetBackgroundColor,
    onLinkClick,
}) => {
    const { publicBrand } = useAuthContext();
    const { showModal } = useContext(ModalContext);

    useEffect(() => {
        handleSetBackgroundColor(backgroundColor);
    });

    const hasImages = Array.isArray(images) && images.length > 0;

    const CustomTableCell = TableCell.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                backgroundColor: {
                    default: null,
                    parseHTML: element =>
                        element.getAttribute('data-background-color'),
                    renderHTML: attributes => {
                        return {
                            'data-background-color': attributes.backgroundColor,
                            style: `background-color: ${attributes.backgroundColor}`,
                        };
                    },
                },
            };
        },
    });

    const editor = useEditor({
        extensions: [
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            TextStyle.configure({ types: [ListItem.name] }),
            FontFamily.configure({
                types: ['textStyle'],
            }),
            FontSize,
            StarterKit.configure({
                // StarterKit v3 bundles these; keep the explicitly configured ones.
                link: false,
                underline: false,
                trailingNode: false,
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
                },
            }),
            Placeholder.configure({
                placeholder: getText('msg_8b4539a50a3b'),
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                protocols: ['ftp', 'mailto'],
                HTMLAttributes: {
                    class: '',
                },
            }),
            ResizableImage,
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            CustomTableCell,
            Emoji,
            VideoNode,
            QRCodeNode,
            DivParagraph,
        ],
        content: normalizeHtmlForEditorLoad(text),
        editable: false, // READ-ONLY
    });

    if (!editor) {
        return null;
    }

    function normalizeHtmlForEditorLoad(html) {
        if (typeof html !== 'string' || !html) return html || '';

        // Replace any <div> that contains only whitespace and a <br> with an empty <div>
        return html.replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, '<div></div>');
    }

    useEffect(() => {
        if (editor && editor.getHTML() !== text) {
            const normalizedText = normalizeHtmlForEditorLoad(text);
            editor.commands.setContent(normalizedText, { emitUpdate: false });
        }
    }, [text]);

    function handleContentClickCapture(event) {
        const assetAnchor = event.target.closest('a');

        if (!isAssetAnchor(assetAnchor)) {
            return;
        }

        const href = assetAnchor.getAttribute('href');
        if (!href) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        showModal({
            type: ModalTypes.ASSET_VIEW,
            title: assetAnchor.textContent?.trim() || 'Asset Preview',
            text: href,
        });
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                width: '100%',
                marginTop: 'auto',
                marginBottom: 'auto',
                verticalAlign: 'middle',
            }}
        >
            {icon ? (
                // IF icon IS NOT NULL, RENDER ICON DISPLAY OF CHILD PORTAL CONTAINER
                <>
                    <div
                        className="d-flex iconChildView"
                        //TODO: Make icon grow dynamically?
                        style={{ backgroundColor: backgroundColor }}
                    >
                        <i
                            className={'iconChildMedia bi bi-' + icon}
                            style={{ color: foregroundColor }}
                        ></i>
                        <p
                            className="fw-semibold iconChildText"
                            style={{
                                color: foregroundColor,
                                maxLines: '2',
                                lineHeight: '1.3em',
                                padding: '0',
                                margin: '0',
                            }}
                        >
                            {title}
                        </p>
                    </div>
                </>
            ) : (
                // IF icon IS NULL, RENDER MULTIMEDIA COMPOSITE DISPLAY OF CHILD PORTAL CONTAINER
                <>
                    {images !== null ? <ImageCarousel images={images} /> : null}
                    <div
                        style={{
                            margin: hasImages ? '0 20px' : '0px',
                        }}
                        onClickCapture={handleContentClickCapture}
                    >
                        <EditorContent
                            editor={editor}
                            className="custom-readonly-editor child-portal-editor-margins"
                        />
                    </div>

                    <div
                        className="navigation-button button-base button-primary float-end"
                        style={{
                            backgroundColor: publicBrand.accessoryColor,
                            color: publicBrand.accessoryTextColor,
                            cursor: 'pointer',
                            margin: hasImages ? '0 20px' : '0px',
                        }}
                        onClick={e => {
                            e.stopPropagation();
                            if (onLinkClick) {
                                onLinkClick();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onLinkClick) {
                                    onLinkClick();
                                }
                            }
                        }}
                    >
                        {button}
                    </div>
                </>
            )}
        </div>
    );
};

export default ChildPortal;
